import axios from 'axios'

import type { CoiSpeed, InsuranceType, PaymentPlan } from '@/components/quotes/quoteCardTypes'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import type { DocumentType } from './document'
import type { CoverageType } from './insuranceCoverage'
import type { InsuranceQuote } from './insuranceQuote'
import {
  type InsuranceCoverage,
  type InsuranceIndicationStatus,
  type InsuranceOneTimeFee,
  InsuranceQuoteStatus,
  type PremiumRange,
  Vendor,
} from './insuranceTypes'

export interface IndicationSubmissionScreenshot {
  id: string
  fileName: string
  dateCreated: string
  url: string | null
}

/** One file kept from a checkout — the screenshot, or the DOM snapshot behind it. */
export interface CheckoutCapture {
  id: string
  attachmentType: 'CHECKOUT_SCREENSHOT' | 'CHECKOUT_DOM_SNAPSHOT'
  fileName: string
  dateCreated: string
  url: string | null
}

/** What the customer had on screen when they checked out — Cai admins only. */
export interface CheckoutEvidence {
  appliedAt: string | null
  clientContext: Partial<Record<string, string | number | string[]>>
  captures: CheckoutCapture[]
}

/** Claims capabilities as served: a flag per filter, plus the prose the card prints. */
export interface IndicationClaimsCapabilities {
  hasMultipleWaysToFile: boolean
  waysToFile: string
  has247Support: boolean
  supportAvailability: string
  hasPayoutInformation: boolean
  payoutSpeed: string
  waysToGetPaid: string
}

export enum DeclineCategory {
  Appetite = 'APPETITE',
  Geography = 'GEOGRAPHY',
  LossHistory = 'LOSS_HISTORY',
  DriverRecord = 'DRIVER_RECORD',
  OperatingHistory = 'OPERATING_HISTORY',
  DataIncomplete = 'DATA_INCOMPLETE',
  AlreadyInsured = 'ALREADY_INSURED',
  NeedsInfo = 'NEEDS_INFO',
  Unknown = 'UNKNOWN',
}

export interface DeclineDetail {
  category: DeclineCategory
  staffNote: string
  isActionable: boolean
  /** Corgi: each decline code in plain words. Rocklake: the decline phrase from the portal row. */
  carrierReason: string | null
  /** False when the carrier gave no reason Cai can name. */
  reasonKnown: boolean
}

export interface InsuranceIndication {
  id: string
  dateCreated: string
  dateUpdated: string
  dateApplied: string
  /** When we actually sent this application to the market. Null until it goes out. */
  dateSubmitted: string | null
  /** Policy start date (YYYY-MM-DD) the carrier priced; null when unknown. */
  pricedStartDate: string | null
  vendor: Vendor
  vendorId: string
  status: InsuranceIndicationStatus
  vendorName: string
  broker: string | null
  /** Assigned InsuranceCarrier record; null falls back to vendor branding. */
  carrierId: string | null
  /** One name for every entity this carrier writes on; null where the row names no carrier
   *  and its vendor writes none either, so there is no brand to merge its answer under. */
  carrierBrand: string | null
  /** Token grouping a carrier's separately-rated lines into one offer. */
  offerGroup: string | null
  /** The one line this row rated. Null for carriers that rate every line at once. */
  coverageLine: CoverageType | null
  metadata: Record<string, any>
  oneTimeFees: InsuranceOneTimeFee[]
  premium: number
  /** Shown in place of the premium while a Corgi prequote prices it. Optional for deploy skew. */
  premiumRange?: PremiumRange | null
  coverages: InsuranceCoverage[]
  summary: string
  pros: string
  cons: string
  quotes: InsuranceQuote[]
  isCardVisible: boolean
  portalLink: {
    url: string
    sourceLabel: string
    statusLabel: string
    providerLabel: string
  } | null
  iconUrl: string
  iconAlt: string
  perTruckLow: number | null
  perTruckHigh: number | null
  fleetLow: number | null
  fleetHigh: number | null
  /** Computed note shown to the customer under "Notes from <broker>" — this offer's own. */
  carrierNote: string | null
  /** This indication's own note, raw — edit this, not carrierNote. */
  brokerNote: string
  /** A broker marked this offer as the one they're steering the customer toward. */
  brokerRecommended: boolean
  /** Full name of whoever recommended it; null when nobody has. */
  brokerRecommendedByName: string | null
  declineReason: string | null
  /** Verbatim carrier text. Cai admins only — null for everyone else. */
  declineReasonRaw: string | null
  /** Category, staff note and actionability. Cai admins only. */
  declineDetail: DeclineDetail | null
  /** Why a carrier that didn't price failed, as the failure analysis told the customer; null until then. */
  failureReason: string | null
  isCurrentInsurer: boolean
  /** The customer added this quote themselves, from another broker. */
  isCustomerUploaded: boolean
  /** Curated carrier profile, resolved from the assigned carrier or the vendor's record. */
  carrierRating: string | null
  coiSpeed: CoiSpeed | null
  /** The paper a brand quoted on, when it quotes on several at once, e.g. "APEX". */
  carrierProgram: string | null
  /** Whether the state guaranty fund backstops this carrier's paper. */
  insuranceType: InsuranceType | null
  /** Free text describing the financing on offer; null when the carrier offers none. */
  financingOptions: string | null
  /** The carrier's own payment plans for the policy, read off its quote (GEICO) or its portal (Corgi, Cover Whale, Rocklake). */
  paymentPlans?: { plans: PaymentPlan[]; note: string | null } | null
  /** The carrier prices each coverage separately, so each gets its own "Add to cart". */
  allowsIndividualCoveragePurchase: boolean
  /** Set when the physical damage limit is the total value of this many units. */
  physicalDamageUnitCount?: number | null
  claimsCapabilities: IndicationClaimsCapabilities | null
  /** The carrier's own curated note, shown in Carrier details — independent of carrierNote. */
  carrierPublicNote: string | null
  sourceDocumentUrl: string | null
  aiSummary: string | null
  submissionScreenshots?: IndicationSubmissionScreenshot[]
  /** The page the customer checked out from — Cai admins only. */
  checkoutEvidence?: CheckoutEvidence | null
  /** Rocklake underwriter note text — staff-only, until portal-notes replies ship. */
  additionalInfoRequested: string | null
  /** Deep link to the Rocklake portal note page — staff-only. */
  portalNoteUrl: string | null
  /** Quote PDFs attached from Rocklake email ingestion — staff-only. */
  quoteDocuments?: IndicationSubmissionScreenshot[]
  /** The carrier's own quote PDF for this offer, when one exists. */
  quoteDocument: { url: string; dateCreated: string } | null
  /** The carrier's own terms are still waiting on the customer — drives the card's Review banner. */
  needsAttestation: boolean
  /** Staff only: this card priced an older version of the application. Optional for deploy skew. */
  isOutOfDate?: boolean
  /**
   * Document types the carrier is holding this offer for, as {@link DocumentType} values.
   * Drives the card's upload banner — a carrier waiting on a file it never gets never prices.
   */
  outstandingDocuments?: DocumentType[]
  /**
   * What the carrier expects this to cost, priced off public fleet data while it waits on
   * {@link outstandingDocuments}. Served only while a document is outstanding, and never
   * checkoutable: it is priced without the loss history that file carries.
   */
}

/** A Corgi prequote — a price to weigh the upload against, not an offer. */

const REAL_VENDOR_NAMES = new Set(['Nirvana Insurance', 'Corgi', 'Cover Whale'])

export function isDemoVendor(vendorName: string): boolean {
  return !REAL_VENDOR_NAMES.has(vendorName)
}

export function getActiveQuote(indication: InsuranceIndication): InsuranceQuote | null {
  if (!indication.quotes?.length) return null
  // Priority: BOUND (highest) → REQUEST_TO_BIND → QUOTED → fallback
  return (
    indication.quotes.find((q) => q.status === InsuranceQuoteStatus.BOUND) ||
    indication.quotes.find((q) => q.status === InsuranceQuoteStatus.REQUEST_TO_BIND) ||
    indication.quotes.find((q) => q.status === InsuranceQuoteStatus.QUOTED) ||
    indication.quotes[0]
  )
}

/** A "completed" indication is one that has produced at least one quote. */
export function isCompletedIndication(indication: InsuranceIndication): boolean {
  return (indication.quotes?.length ?? 0) > 0
}

export async function generateQuoteFromIndication(orgId: string, indicationId: string): Promise<void> {
  await axios.post(`/api/v1/insure/organisations/${orgId}/indications/${indicationId}/generate-quote/`)
}

export interface IndicationCoveragePayload {
  id?: string
  coverageType: string
  vendorCoverageType: string
  premium: number | null
  limit: number | null
  deductible: number | null
  /** Set only when physical damage has a comprehensive deductible of its own. */
  comprehensiveDeductible?: number | null
  premiumPerUnit: number | null
}

export interface IndicationFeePayload {
  id?: string
  label: string
  amount: number
}

export interface CreateIndicationPayload {
  vendor: Vendor
  carrierId?: string | null
  coverages: IndicationCoveragePayload[]
  oneTimeFees: IndicationFeePayload[]
  sourceDocumentId?: string
}

export async function createIndication(
  orgId: string,
  payload: CreateIndicationPayload
): Promise<InsuranceIndication> {
  const res = await axios.post(`/api/v1/insure/organisations/${orgId}/indications/`, toSnakeCase(payload))
  return res.data
}

export async function deleteIndication(orgId: string, indicationId: string): Promise<void> {
  await axios.delete(`/api/v1/insure/organisations/${orgId}/indications/${indicationId}/`)
}

export async function setIndicationRecommended(
  orgId: string,
  indicationId: string,
  recommended: boolean
): Promise<void> {
  await axios.patch(`/api/v1/insure/organisations/${orgId}/indications/${indicationId}/`, {
    broker_recommended: recommended,
  })
}

export async function setIndicationApplied(
  orgId: string,
  indicationId: string,
  applied: boolean
): Promise<void> {
  await axios.post(`/api/v1/insure/organisations/${orgId}/indications/${indicationId}/apply/`, { applied })
}

export interface SendCorgiDocumentResult {
  documentId: string
  success: boolean
  /** True when the sync worker will send it on its next run, rather than sent already. */
  queued?: boolean
  sentCount: number
  attachmentCount?: number
  error: string | null
}

export async function sendCorgiDocuments(
  orgId: string,
  indicationId: string,
  documentIds: string[]
): Promise<SendCorgiDocumentResult[]> {
  const res = await axios.post(
    `/api/v1/insure/organisations/${orgId}/indications/${indicationId}/send-corgi-documents/`,
    { document_ids: documentIds }
  )
  return (toCamelCase(res.data) as { results: SendCorgiDocumentResult[] }).results
}

/** Staff: ask the sync job to read this Corgi quote again, linking a pasted Corgi quote id if given. */
export async function syncCorgi(orgId: string, indicationId: string, quoteId: string): Promise<void> {
  await axios.post(`/api/v1/insure/organisations/${orgId}/indications/${indicationId}/sync-corgi/`, {
    quote_id: quoteId,
  })
}

export interface ParsedPolicyCoverage {
  coverageType: string
  premium: number | null
  limit: number | null
  deductible: number | null
  comprehensiveDeductible?: number | null
  premiumPerUnit: number | null
}

export interface ParsedPolicyIndication {
  documentId: string
  vendor: Vendor | null
  vendorId: string | null
  /** Carrier name as written on the document. */
  carrierName: string | null
  /** The InsuranceCarrier record that name matched, if any. */
  carrierId: string | null
  coverages: ParsedPolicyCoverage[]
  oneTimeFees: { label: string; amount: number }[]
  warnings: string[]
}

/** Upload a policy document and have the LLM parse it into draft indication fields. */
export async function parsePolicyToIndication(orgId: string, file: File): Promise<ParsedPolicyIndication> {
  const formData = new FormData()
  formData.append('file', file)
  const res = await axios.post(`/api/v1/insure/organisations/${orgId}/indications/parse-policy/`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return toCamelCase(res.data) as ParsedPolicyIndication
}
